/*
 * Countdown for the header bar.
 *
 * Three modes, all driven by data attributes the section writes.
 *
 * Evergreen, the default, works the way custom-offer-countdown.js does: the
 * deadline is stored once per visitor and restarts on its own once it passes,
 * so the bar never shows zeros and a returning visitor does not get a fresh
 * full timer on every page view.
 *
 * End date counts to a fixed moment instead and hides the whole bar once that
 * moment has gone, which is what a dated sale needs.
 *
 * Midnight counts to the end of the visitor's day and starts again the next.
 *
 * A separate file rather than a shared one: the offer banner belongs to work
 * that is already accepted, and this design also needs a days box and the end
 * date mode.
 */
(function () {
  var STORAGE_PREFIX = 'elanora:header-countdown:';
  var UNITS = ['days', 'hours', 'minutes', 'seconds'];
  var memoryStore = {};

  function readDeadline(key) {
    try {
      return window.localStorage.getItem(STORAGE_PREFIX + key);
    } catch (error) {
      return memoryStore[key] || null;
    }
  }

  function writeDeadline(key, value) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (error) {
      memoryStore[key] = value;
    }
  }

  function evergreenDeadline(element) {
    var key = element.dataset.countdownKey || 'default';
    var hours = parseFloat(element.dataset.countdownHours) || 0;
    var minutes = parseFloat(element.dataset.countdownMinutes) || 0;
    // KK-2455. Whole minutes, so 8 h 23 min is exactly 30180 seconds.
    var totalMinutes = Math.round(hours * 60 + minutes);

    if (totalMinutes <= 0) return null;

    var deadline = parseInt(readDeadline(key), 10);

    if (!deadline || isNaN(deadline) || deadline <= Date.now()) {
      deadline = Date.now() + totalMinutes * 60 * 1000;
      writeDeadline(key, String(deadline));
    }

    return deadline;
  }

  /*
   * The merchant types a date and a time with no zone, so it is read in the
   * visitor's own zone: a sale that ends at midnight ends at midnight wherever
   * the visitor is, which is what the copy on the bar promises.
   */
  function fixedDeadline(element) {
    var date = (element.dataset.countdownEndDate || '').trim();
    var time = (element.dataset.countdownEndTime || '').trim() || '23:59';

    if (!date) return null;

    var parsed = new Date(date + 'T' + time);
    if (isNaN(parsed.getTime())) return null;

    return parsed.getTime();
  }

  /*
   * KK-2484. Counts to the next midnight on the visitor's own clock, the way
   * Smooche's bar does. Once midnight passes the tick asks again and gets the
   * next one, so the bar restarts at about 24 hours and never hides.
   */
  function midnightDeadline() {
    var midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  }

  function isFixedMode(element) {
    return element.dataset.countdownMode === 'end_date';
  }

  function deadlineFor(element) {
    if (element.dataset.countdownMode === 'midnight') return midnightDeadline();
    return isFixedMode(element) ? fixedDeadline(element) : evergreenDeadline(element);
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function hide(element) {
    var bar = element.closest('.custom-header-countdown');
    if (bar) bar.hidden = true;
  }

  function start(element) {
    if (element.dataset.countdownStarted) return;

    var fixed = isFixedMode(element);
    var values = {};
    var timer = null;

    UNITS.forEach(function (unit) {
      values[unit] = element.querySelector('[data-countdown-value="' + unit + '"]');
    });

    // The days box is left out of the markup on a countdown that cannot reach a
    // day, so only the other three are required.
    if (!values.hours || !values.minutes || !values.seconds) return;

    var deadline = deadlineFor(element);

    if (!deadline) {
      if (fixed) hide(element);
      return;
    }

    element.dataset.countdownStarted = 'true';

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    function tick() {
      // The theme editor swaps the section out on every setting change. The old
      // element keeps its interval, so it stops itself once it is off the page.
      if (!element.isConnected) {
        stop();
        return;
      }

      var remaining = deadline - Date.now();

      if (remaining <= 0) {
        if (fixed) {
          stop();
          hide(element);
          return;
        }

        deadline = deadlineFor(element);
        if (!deadline) {
          stop();
          return;
        }
        remaining = deadline - Date.now();
      }

      var totalSeconds = Math.floor(remaining / 1000);
      var hours = Math.floor((totalSeconds % 86400) / 3600);

      // With no days box the hours keep counting past 24 rather than rolling
      // over into a number nothing on the bar shows.
      if (!values.days) {
        hours = Math.floor(totalSeconds / 3600);
      } else {
        values.days.textContent = pad(Math.floor(totalSeconds / 86400));
      }

      values.hours.textContent = pad(hours);
      values.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      values.seconds.textContent = pad(totalSeconds % 60);
    }

    tick();

    // A date that had already passed hides the bar on that first tick, and then
    // there is nothing left to count.
    var bar = element.closest('.custom-header-countdown');
    if (bar && bar.hidden) return;

    timer = window.setInterval(tick, 1000);
  }

  function init() {
    // KK-2455. A slider copy of the timer slide is made after the original has
    // started and carries its started mark; clear it so the copy ticks too.
    document.querySelectorAll('.swiper-slide-duplicate [data-header-countdown]').forEach(function (copy) {
      delete copy.dataset.countdownStarted;
    });

    document.querySelectorAll('[data-header-countdown]').forEach(start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', init);

  // KK-2455. The slide is inserted into the announcement bar after this script
  // has run, so the bar starts the timer itself once the slide is in.
  window.ElanoraHeaderCountdown = { init: init };
})();
